import React from "react";
import { MediaAccordion } from "ui/accordion";
import {
  InsightsAnimation,
  TraitsAnimation,
  JourneysAnimation,
  SplitsAnimationWithBg,
  type InsightsAnimationProps,
} from "components/animations";
import { FillImage } from "components/images";
import { Box, type SpaceProps } from "@chakra-ui/react";
import { TextTitle, type TextTitleProps } from "ui/text";
import measurePerformanceImage from "../../../public/assets/platform/composable-cdp/measure-performance.png";

type Props = {
  insightsAnimationProps?: InsightsAnimationProps;
} & Pick<TextTitleProps, "theme" | "title" | "description"> &
  SpaceProps;

export function HightouchCdpFeaturesAccordion({
  theme = "dark",
  title,
  description,
  insightsAnimationProps,
  ...spaceProps
}: Props) {
  return (
    <Box {...spaceProps}>
      <TextTitle
        theme={theme}
        size="lg"
        align="center"
        title={title}
        description={description}
        mb={{ base: "bigger", md: "huge" }}
      />
      <MediaAccordion
        prerenderMedia={false}
        direction="right"
        variant="dynamic"
        theme={theme}
        items={[
          {
            title: "Access any data and uncover insights",
            content: "Marketers get full self-serve access to customer data to power campaigns and personalization",
            media: <InsightsAnimation delay={0.3} {...insightsAnimationProps} />,
          },
          {
            title: "Build traits and audiences",
            content:
              "Create audiences with easily defined new calculated fields for personalization and segmentation like ‘Last Page Viewed’ or ‘Lifetime Value’ without code.",
            media: <TraitsAnimation delay={0.3} />,
          },
          {
            title: "Orchestrate journeys",
            content:
              "Easily prioritize campaigns across your audiences. Ensure that customers experience cohesive marketing and get the right message at the right time.",
            media: <JourneysAnimation delay={0.3} />,
          },
          {
            title: "Run experiments",
            content:
              "Build randomized audience splits directly within Hightouch to centrally manage your multivariate testing across channels. Never upload a testing CSV again.",
            media: <SplitsAnimationWithBg delay={0.3} />,
          },
          {
            title: "Measure performance",
            content:
              "Measure, explore, and analyze customer data and campaign performance, directly from the data warehouse on a campaign, audience, or user level.",
            media: (
              <FillImage
                priority
                src={measurePerformanceImage}
                alt="Illustration of Hightouch UI with a dashboard displaying three different campaign / ad sets with their respective spend, ROAS, and revenue values displayed both numerically and as line charts."
              />
            ),
          },
        ]}
      />
    </Box>
  );
}
